import type { ScaleLinear } from "d3";
import type { JSX } from "react/jsx-runtime";

import type { PathwayAssessment } from "@thalamos/aspire-common";

/**
 * The assessment swimlane: the run-up to a detention, drawn as its own row.
 *
 * It cannot be a level on the section scale, because an assessment overlaps a
 * detention by nature — a patient held under a holding power or 136 is assessed
 * for admission under another section — and the section line carries one value at
 * a time. The lane sits adjacent to the low end of the section scale so that
 * "assessed while held under 136" reads as two vertically-aligned marks.
 *
 * Deliberately reduced to rules and ticks. A patient under repeated assessment
 * has many overlapping spans, and anything with more visual weight than a
 * hairline turns that into noise at full width. What the lane answers is WHEN
 * assessment activity happened and how long it ran; the outcome, the counts and
 * the provenance are all on hover, where they cost nothing until asked for. Death
 * needs no mark — the chart already draws a death line across every lane.
 */

/** One ink, deliberately not a section or trust hue: this is not a section. */
const ASSESSMENT_INK = "#475569";

/** The span rule. Thin enough that stacked assessments still read as separate. */
const RULE_HEIGHT = 2;

/** A lane this tall has room for the per-signature ticks; the grid's does not. */
const SIGNATURE_TICK_MIN_HEIGHT = 12;

/**
 * Sub-rows the lane will split into. Assessments that overlap must not share a
 * row: drawn at one height they collapse into a single indistinguishable rule,
 * and the later one's hit target covers the earlier, making data that is on
 * screen impossible to inspect. Two rows separates the overwhelming majority of
 * patients; beyond that the concurrency is the finding, and no amount of stacking
 * makes it legible in a few pixels.
 */
const MAX_ROWS = 2;

/** Clear space between two spans sharing a row, so they read as two. */
const ROW_GAP_PX = 1;

export type AssessmentHover = {
  xPos: number;
  assessment: PathwayAssessment;
};

/**
 * Assign each span a sub-row so that no two that touch share one, using at most
 * `maxRows`. Greedy first-fit by left edge, which for intervals needs exactly the
 * peak-overlap number of rows — no arrangement does better, so there is nothing
 * cleverer to reach for.
 *
 * Packing is over RENDERED extents, not dates: on a multi-year axis two
 * assessments a week apart are a pixel apart, so temporal separation is not
 * visual separation.
 *
 * Returns the row per span, in the order the spans were given. Which row a span
 * lands in is an artifact of the packing, not a fact about the assessment.
 */
export function packRows(
  spans: { x1: number; x2: number }[],
  maxRows: number,
): number[] {
  const order = spans
    .map((s, i) => ({ s, i }))
    .sort((a, b) => a.s.x1 - b.s.x1 || a.s.x2 - b.s.x2);
  const rowEnds: number[] = [];
  const rows = new Array<number>(spans.length).fill(0);

  for (const { s, i } of order) {
    let row = rowEnds.findIndex((end) => end + ROW_GAP_PX <= s.x1);
    if (row === -1 && rowEnds.length < maxRows) {
      row = rowEnds.length;
      rowEnds.push(Number.NEGATIVE_INFINITY);
    }
    if (row === -1) {
      // Past the row budget: take the row that frees up soonest, so whatever
      // overlap has to remain is the least of the options.
      row = rowEnds.reduce(
        (best, end, r) => (end < best.end ? { row: r, end } : best),
        { row: 0, end: Number.POSITIVE_INFINITY },
      ).row;
    }
    rowEnds[row] = Math.max(rowEnds[row] ?? Number.NEGATIVE_INFINITY, s.x2);
    rows[i] = row;
  }
  return rows;
}

export const AssessmentLane = ({
  assessments,
  xScale,
  boundsWidth,
  height,
  onHover,
}: {
  assessments: PathwayAssessment[];
  xScale: ScaleLinear<number, number>;
  boundsWidth: number;
  height: number;
  onHover?: (hover: AssessmentHover | null) => void;
}): JSX.Element | null => {
  if (assessments.length === 0) return null;
  const clamp = (x: number): number => Math.max(0, Math.min(boundsWidth, x));
  const showSignatures = height >= SIGNATURE_TICK_MIN_HEIGHT;

  // The axis is not guaranteed to cover a patient's whole history, so an
  // assessment can fall entirely outside it. Clamping one to the nearest edge
  // would draw it as though it happened at the edge of the window — a mark
  // asserting the wrong date — so it is dropped instead. One that merely
  // straddles an edge is clipped, which is honest.
  const visible = assessments.filter((a) => {
    // An unresolved assessment with no examination on record has neither a span
    // to draw nor a resolution to mark, so it is excluded rather than emitting an
    // element that renders as nothing. It still exists in the data.
    if (a.kind === "marker" && a.endReason === "open") return false;
    const end = xScale(new Date(a.endAt).getTime());
    const start =
      a.startAt === null ? end : xScale(new Date(a.startAt).getTime());
    return end >= 0 && start <= boundsWidth;
  });
  if (visible.length === 0) return null;

  const extents = visible.map((assessment) => {
    const endX = clamp(xScale(new Date(assessment.endAt).getTime()));
    const startX =
      assessment.startAt === null
        ? null
        : clamp(xScale(new Date(assessment.startAt).getTime()));
    // A minimum width keeps a sub-day assessment visible: an assessment runs for
    // days, so on a multi-year axis most spans round to nothing and would
    // silently disappear. Pack against that drawn width, not the true one.
    const drawnWidth = startX === null ? 0 : Math.max(1.5, endX - startX);
    return { assessment, startX, endX, drawnWidth };
  });

  const rows = packRows(
    extents.map((e) => ({
      x1: e.startX ?? e.endX,
      x2: (e.startX ?? e.endX) + e.drawnWidth,
    })),
    MAX_ROWS,
  );
  const lanes = extents.flatMap((extent, i) => {
    const row = rows[i];
    return row === undefined ? [] : [{ ...extent, row }];
  });
  // Only split the lane when something actually needed the second row, so the
  // great majority of patients keep a single centred rule.
  const rowsUsed = Math.max(...rows) + 1;
  const slotHeight = height / rowsUsed;
  const centreOf = (row: number): number => slotHeight * row + slotHeight / 2;
  const halfSlot = Math.max(RULE_HEIGHT / 2, slotHeight / 2 - 0.5);

  return (
    <g>
      {lanes.map(({ assessment: a, startX, endX, drawnWidth, row }) => {
        const cy = centreOf(row);
        const hover = onHover
          ? {
              onMouseEnter: () => {
                onHover({ xPos: startX ?? endX, assessment: a });
              },
              onMouseLeave: () => {
                onHover(null);
              },
            }
          : {};

        return (
          <g key={a.formContextId} {...hover}>
            {startX !== null && (
              <>
                <rect
                  x={startX}
                  y={cy - RULE_HEIGHT / 2}
                  width={drawnWidth}
                  height={RULE_HEIGHT}
                  rx={RULE_HEIGHT / 2}
                  fill={ASSESSMENT_INK}
                  opacity={0.75}
                />
                {showSignatures &&
                  a.signatures.map((s, si) => {
                    const sx = clamp(xScale(new Date(s.signedAt).getTime()));
                    if (sx < startX || sx > endX) return null;
                    return (
                      <line
                        key={`${s.templateId}-${String(s.part)}-${String(si)}`}
                        x1={sx}
                        x2={sx}
                        y1={cy - halfSlot * 0.7}
                        y2={cy + halfSlot * 0.7}
                        stroke={ASSESSMENT_INK}
                        strokeWidth={1}
                        opacity={0.55}
                        pointerEvents="none"
                      />
                    );
                  })}
              </>
            )}
            {/* Where the assessment resolved. A still-open one has not resolved,
                so it gets no tick and its rule simply runs on. Confined to its own
                sub-row, so it never reads as belonging to the row above. */}
            {a.endReason !== "open" && (
              <line
                x1={endX}
                x2={endX}
                y1={cy - halfSlot}
                y2={cy + halfSlot}
                stroke={ASSESSMENT_INK}
                strokeWidth={1}
                opacity={0.9}
              />
            )}
            {/* Invisible hit target, kept inside the sub-row: a lane-tall one would
                cover the rows either side and make them unhoverable. */}
            <rect
              x={Math.min(startX ?? endX, endX) - 3}
              y={cy - slotHeight / 2}
              width={Math.abs(endX - (startX ?? endX)) + 10}
              height={slotHeight}
              fill="transparent"
            />
          </g>
        );
      })}
    </g>
  );
};
